/**
 * The Teams list keeps its filters, sort and page in the URL so a filtered list can be linked to
 * (`/teams?governing_body_id=3`) and survives reloads. URL keys are snake_case; API params are
 * camelCase. Defaults are left out of the URL. Contract: docs/drafts/teams-admin-filters.md §5.
 */

export const DEFAULT_SIZE = 25;
const MAX_SIZE = 200;

export const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "ALL", label: "All" },
];

export const DEFAULT_STATE = {
  query: "",
  status: "ACTIVE",
  governingBodyId: "",
  teamType: "",
  divisionId: "",
  country: "",
  state: "",
  city: "",
  sort: "Id",
  isAscending: true,
  page: 0,
  size: DEFAULT_SIZE,
};

/** state key -> URL key, for the plain string filters. */
const URL_KEYS = {
  query: "q",
  governingBodyId: "governing_body_id",
  teamType: "team_type",
  divisionId: "division_id",
  country: "country",
  state: "state",
  city: "city",
};

const STATUSES = new Set(STATUS_OPTIONS.map((o) => o.value));

const nonNegativeInt = (value, fallback) => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : fallback;
};

/**
 * Parses `location.search`. Invalid values fall back to the default instead of erroring, so a
 * mistyped link still opens the list. A state without a country is dropped: the State filter
 * is chosen from the selected country's list.
 */
export const readQuery = (search) => {
  const sp = new URLSearchParams(search);
  const get = (key) => (sp.get(key) || "").trim();

  const result = { ...DEFAULT_STATE };
  Object.entries(URL_KEYS).forEach(([stateKey, urlKey]) => {
    result[stateKey] = get(urlKey);
  });

  if (!/^\d+$/.test(result.governingBodyId)) result.governingBodyId = "";
  result.teamType = result.teamType.toUpperCase();
  result.country = result.country.toUpperCase();
  result.state = result.country ? result.state.toUpperCase() : "";

  const status = get("status").toUpperCase();
  result.status = STATUSES.has(status) ? status : DEFAULT_STATE.status;

  result.sort = get("sort") || DEFAULT_STATE.sort;
  result.isAscending = get("dir").toLowerCase() !== "desc";
  result.page = nonNegativeInt(get("page"), 0);
  const size = nonNegativeInt(get("size"), DEFAULT_SIZE);
  result.size = size > 0 && size <= MAX_SIZE ? size : DEFAULT_SIZE;
  return result;
};

/** The URL search string for `state`, defaults omitted (`""` when nothing is set). */
export const writeQuery = (state) => {
  const sp = new URLSearchParams();
  Object.entries(URL_KEYS).forEach(([stateKey, urlKey]) => {
    const value = String(state[stateKey] ?? "").trim();
    if (value) sp.set(urlKey, value);
  });
  if (state.status && state.status !== DEFAULT_STATE.status) {
    sp.set("status", state.status);
  }
  if (state.sort && state.sort !== DEFAULT_STATE.sort)
    sp.set("sort", state.sort);
  if (!state.isAscending) sp.set("dir", "desc");
  if (state.page) sp.set("page", String(state.page));
  if (state.size && state.size !== DEFAULT_SIZE) {
    sp.set("size", String(state.size));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

/** Params for `teamAdminApi.list`; blanks are dropped by its query builder. */
export const toApiParams = (state) => ({
  page: state.page,
  size: state.size,
  sort: state.sort,
  isAscending: state.isAscending,
  status: state.status,
  query: state.query,
  teamType: state.teamType,
  governingBodyId: state.governingBodyId,
  divisionId: state.divisionId,
  city: state.city,
  state: state.country ? state.state : "",
  country: state.country,
});

/** True when any filter differs from the default list (sort and paging excluded). */
export const hasFilters = (state) =>
  state.status !== DEFAULT_STATE.status ||
  Object.keys(URL_KEYS).some((key) => String(state[key] ?? "").trim() !== "");

/** Filter values reset to the defaults, keeping sort and page size. */
export const clearedFilters = (state) => ({
  ...DEFAULT_STATE,
  sort: state.sort,
  isAscending: state.isAscending,
  size: state.size,
});
